import { cn } from '@src/lib/utils';
import { Trans } from '@lingui/react/macro';
import { FlowData, FlowElementTypes } from '@sdk';
import { Activity, AlertTriangle, ChevronDown, ChevronRight, Sparkles, Wrench } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { pairToolEvents, type ToolPair } from './groupTurnEvents';
import { describeEvent } from './toolEventDescriptor';
import { useChipTarget } from './useChipTarget';
import { useLinkHandlers } from '@src/components/links/LinkHandlersContext';
import { LinkifiedText } from '@src/components/links/LinkifiedText';

interface ToolEntryRowProps {
  events: FlowData[];
}

interface OneLiner {
  icon: LucideIcon;
  /** Errors render in the destructive colour; everything else is muted. */
  isError?: boolean;
  label: string;
  detail: string;
  inFlight: boolean;
}

/**
 * Dense one-row summary of every non-text, non-reasoning event in an
 * assistant turn: tool calls, hook status, errors. Defaults to `count +
 * latest one-liner`; an expand chevron reveals every event, and individual
 * tool entries can expand again to show full args + result JSON.
 *
 * Reasoning is grouped separately by `groupTurnEvents` and rendered by
 * `ThinkingSummary` — the REASONING handling below is defensive only.
 */
export function ToolEntryRow({ events }: ToolEntryRowProps) {
  const [expanded, setExpanded] = useState(false);

  const { pairs, total: totalCount } = useMemo(() => pairToolEvents(events), [events]);

  const latest = useMemo<OneLiner | null>(() => describeLatest(events, pairs), [events, pairs]);

  if (totalCount === 0) return null;

  // Compact chip: a tiny pill that signals "agent is alive and doing things".
  // Visual goal is heartbeat / breadcrumb, not a primary CTA. Click expands
  // to the full per-event list, but expansion is intentionally low-affordance
  // (no chevron in the resting state — discovery is by hover).
  const headlineDetail = latest?.detail ?? '';
  return (
    <div
      data-testid="dense-tool-row"
      // Column so the expanded list drops BELOW the chip, indented slightly,
      // instead of stretching to the right and turning the chip into a giant
      // strip. `items-start` keeps the chip pill content-sized.
      className="my-0.5 flex flex-col items-start gap-1"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="dense-tool-row-toggle"
        aria-expanded={expanded}
        title={
          latest
            ? `${totalCount} event${totalCount === 1 ? '' : 's'} · ${latest.label}${headlineDetail ? ` · ${headlineDetail}` : ''}`
            : `${totalCount} event${totalCount === 1 ? '' : 's'}`
        }
        className={[
          'inline-flex max-w-full items-center gap-1.5 rounded-md border border-border/60 px-2 py-1',
          'text-[13px] leading-none text-muted-foreground',
          'bg-muted/40 hover:bg-muted hover:text-foreground',
          'transition-colors',
        ].join(' ')}
      >
        <OneLinerIcon icon={latest?.icon ?? Wrench} isError={latest?.isError} inFlight={latest?.inFlight ?? false} />
        {latest && <span className="whitespace-nowrap font-medium">{latest.label}</span>}
        {headlineDetail && (
          <span className="max-w-[260px] truncate font-mono text-[12px] text-muted-foreground/80">
            {headlineDetail}
          </span>
        )}
        {totalCount > 1 && <span className="text-[11px] tabular-nums opacity-50">·&nbsp;{totalCount}</span>}
      </button>

      {expanded && (
        <div className="ms-3 max-w-full rounded-md border border-border/60 bg-muted/30 px-2 py-1">
          <TurnEventList events={events} />
        </div>
      )}
    </div>
  );
}

/**
 * The per-event list for one turn's dense events: paired TOOL_CALL/TOOL_RESULT
 * rows, other events (status / error), then orphan results. Every
 * row is click-expandable to its pretty-printed payload (JSON-string payloads
 * are parsed and re-indented — the one-liner detail is truncated, so the
 * expansion is the only way to actually read an init / rate-limit event).
 * Shared by the inline {@link ToolEntryRow} (its expanded state) and the vibe
 * footer's `TurnEventChip` popover so both open to an identical list. Renders
 * nothing when the turn has no dense events.
 */
export function TurnEventList({ events }: { events: FlowData[] }) {
  const { pairs, others, orphanResults, total } = useMemo(() => pairToolEvents(events), [events]);
  if (total === 0) return null;
  return (
    <ul className="flex max-w-full flex-col gap-0.5">
      {pairs.map((pair, i) => (
        <ToolPairItem key={`pair-${i}`} pair={pair} />
      ))}
      {others.map((evt, i) => (
        <OtherEventItem key={`other-${i}`} event={evt} />
      ))}
      {orphanResults.map((evt, i) => (
        <OrphanResultItem key={`orphan-${i}`} event={evt} />
      ))}
    </ul>
  );
}

function OneLinerIcon({ icon: Icon, isError, inFlight }: Omit<OneLiner, 'label' | 'detail'>) {
  // In-flight = a TOOL_CALL has no matching TOOL_RESULT yet. Pulse the SAME
  // icon as the resting state rather than swapping in a spinner, so the row
  // reads as activity, not "busy/loading".
  const tone = isError ? 'text-destructive' : inFlight ? 'animate-pulse text-foreground' : 'text-muted-foreground';
  return <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${tone}`} />;
}

/**
 * One expandable event row: a friendly icon+label line with a chevron toggle
 * revealing the pretty-printed payload below.
 */
function ExpandableRow({
  children,
  payload,
  trailing,
}: {
  children: React.ReactNode;
  payload: React.ReactNode;
  /**
   * Rendered beside the toggle, NOT inside it — a chip's "open the thing I
   * touched" link is its own affordance and must not nest inside the
   * expander's button.
   */
  trailing?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex w-full items-center gap-1.5 px-1.5 py-1">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 items-center gap-1.5 text-start"
        >
          {open ? (
            <ChevronDown className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
          )}
          {children}
        </button>
        {trailing}
      </div>
      {open && <div className="px-2 pb-1 pt-0.5">{payload}</div>}
    </>
  );
}

function ToolPairItem({ pair }: { pair: ToolPair }) {
  const desc = useMemo(() => describeEvent(pair.call), [pair.call]);
  const openTarget = useChipTarget(desc.target);
  // Replay emits durable semantic operations (file_write, file_edit,
  // skill_call, etc.) as TOOL_CALL-shaped rows. They describe work that has
  // already happened and do not have a separate TOOL_RESULT to pair with.
  // Only a raw tool_use without a result is genuinely still in flight.
  const inFlight = pair.result === null && !pair.terminated && pair.call.attributes.subtype === 'tool_use';
  const isError = pair.result?.attributes['outcome'] === 'error' || pair.result?.attributes['is_error'] === 'true';
  const resultData = pair.result?.data;
  const resultOutput =
    resultData && typeof resultData === 'object' && 'content' in resultData
      ? (resultData as { content?: unknown }).content
      : resultData;

  return (
    <li
      data-testid="tool-entry"
      data-state={inFlight ? 'running' : isError ? 'error' : 'done'}
      className="rounded border border-transparent text-[13px] hover:border-border/60"
    >
      <ExpandableRow
        payload={
          <>
            <PayloadBlock
              label="input"
              value={
                (pair.call.data as Record<string, unknown> | undefined)?.args ??
                (pair.call.data as Record<string, unknown> | undefined)?.input
              }
            />
            <PayloadBlock label={inFlight ? 'output (running…)' : 'output'} value={resultOutput} />
          </>
        }
        trailing={
          desc.detail && openTarget ? (
            <button
              type="button"
              data-testid="tool-entry-target"
              title={desc.detail}
              onClick={openTarget}
              className="min-w-0 flex-1 truncate text-start font-mono text-[12px] text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
            >
              {desc.detail}
            </button>
          ) : undefined
        }
      >
        <OneLinerIcon icon={isError ? AlertTriangle : desc.icon} isError={isError} inFlight={inFlight} />
        <span className="flex-shrink-0 whitespace-nowrap font-medium text-foreground">{desc.label}</span>
        {desc.detail && !openTarget && (
          <span className="truncate font-mono text-[12px] text-muted-foreground">{desc.detail}</span>
        )}
      </ExpandableRow>
    </li>
  );
}

function OtherEventItem({ event }: { event: FlowData }) {
  const { icon, label, isError } = describeOther(event);
  const detail = extractText(event);

  return (
    <li
      data-testid="tool-entry"
      data-state="done"
      className="rounded border border-transparent text-[13px] hover:border-border/60"
    >
      <ExpandableRow
        payload={
          <>
            <PayloadBlock label="data" value={event.data} />
            {Object.keys(event.attributes).length > 0 && <PayloadBlock label="attributes" value={event.attributes} />}
          </>
        }
      >
        <OneLinerIcon icon={icon} isError={isError} inFlight={false} />
        <span className="font-medium text-foreground">{label}</span>
        {detail && <span className="truncate font-mono text-[12px] text-muted-foreground">{detail}</span>}
      </ExpandableRow>
    </li>
  );
}

function OrphanResultItem({ event }: { event: FlowData }) {
  return (
    <li data-testid="tool-entry" data-state="done" className={cn('px-1.5 py-0.5 text-[11px] text-muted-foreground')}>
      <span className="opacity-60">
        <Trans>tool result (no matching call):</Trans>
      </span>{' '}
      <span className="truncate font-mono text-[10px]">{extractText(event)}</span>
    </li>
  );
}

function PayloadBlock({ label, value }: { label: string; value: unknown }) {
  const links = useLinkHandlers();
  if (value === null || value === undefined) return null;
  // Status-style events (init, rate-limit, …) carry their payload as a JSON
  // *string* — parse it back so the block shows indented JSON, not one long line.
  let parsed = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        /* not JSON — render the string as-is */
      }
    }
  }
  let text: string;
  try {
    text = typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2);
  } catch {
    text = '[Unserializable payload]';
  }
  if (!text.trim()) return null;
  return (
    <div className="mt-1">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <pre className="mt-0.5 max-h-48 overflow-auto rounded bg-muted/40 px-2 py-1 font-mono text-[10px] leading-snug">
        {links ? <LinkifiedText text={text} handlers={links} /> : text}
      </pre>
    </div>
  );
}

function describeLatest(events: FlowData[], pairs: ToolPair[]): OneLiner | null {
  if (events.length === 0) return null;
  // Two-pass walk so the chip prefers a TOOL_CALL one-liner (e.g.
  // "Bash · ls -la") even when the actual last event in the buffer is a
  // hook STATUS like "PostToolUse". Hook events trail every tool call but
  // make for a poor one-line summary on the chip; the user wants the
  // command/path/url to read.
  for (let i = events.length - 1; i >= 0; i--) {
    const evt = events[i];
    if (evt.elementType !== FlowElementTypes.TOOL_CALL) continue;
    const id = (evt.data as { tool_call_id?: string } | undefined)?.tool_call_id;
    const matchingPair = pairs.find((p) => {
      const pid = (p.call.data as { tool_call_id?: string } | undefined)?.tool_call_id;
      return pid && pid === id;
    });
    const inFlight = !!matchingPair && matchingPair.result === null;
    // Same descriptor the expanded rows use, so the collapsed headline names
    // the operation the same way ("Read · path", not "Using Read").
    const desc = describeEvent(evt);
    return { icon: desc.icon, label: desc.label, detail: desc.detail, inFlight };
  }
  // No tool calls in the bucket — fall back to the latest reasoning /
  // status / error entry. The label alone is the chip detail; we never
  // dump the payload here (long JSON kills the chip layout).
  for (let i = events.length - 1; i >= 0; i--) {
    const evt = events[i];
    if (DENSE_OTHER.has(evt.elementType)) {
      const other = describeOther(evt);
      return { ...other, detail: other.label, inFlight: false };
    }
  }
  return null;
}

const DENSE_OTHER = new Set<string>([FlowElementTypes.REASONING, FlowElementTypes.STATUS, FlowElementTypes.ERROR]);

function describeOther(event: FlowData): { icon: LucideIcon; label: string; isError?: boolean } {
  if (event.elementType === FlowElementTypes.REASONING) return { icon: Sparkles, label: 'thinking' };
  if (event.elementType === FlowElementTypes.ERROR) return { icon: AlertTriangle, label: 'error', isError: true };
  // STATUS — surface the subtype if present (e.g. "PreToolUse"), except where
  // the subtype names something the user is actively waiting on and deserves
  // words rather than a machine token.
  const subtype = event.attributes['subtype'];
  if (subtype === 'credential_refresh') return { icon: Activity, label: 'refreshing credentials' };
  return { icon: Activity, label: subtype || 'status' };
}

function extractText(event: FlowData): string {
  // Hook events from the sniffer carry a pre-built one-liner.
  const msg = event.attributes['hook-message'] || event.attributes['tool-input-summary'];
  if (msg) return String(msg).slice(0, 120);
  if (typeof event.content === 'string' && event.content) return event.content.slice(0, 120);
  if (typeof event.data === 'string') return event.data.slice(0, 120);
  return '';
}
